import * as React from 'react';
import { useField } from 'formik';
import { Textarea, type TextareaProps } from '../primitives';
import { FormField } from './FormField';

export interface FormTextareaProps extends Omit<TextareaProps, 'error' | 'name'> {
  name: string;
  label?: React.ReactNode;
  help?: React.ReactNode;
  fieldClassName?: string;
  /** Show a "used/max" character counter under the field (requires maxLength). */
  showCounter?: boolean;
}

/** Formik-bound textarea built on the design-system `Textarea`. */
export const FormTextarea: React.FC<FormTextareaProps> = ({
  name,
  label,
  help,
  required,
  id,
  fieldClassName,
  showCounter,
  ...props
}) => {
  const [field, meta] = useField(name);
  const error = meta.touched ? meta.error : undefined;
  const inputId = id ?? name;
  const value: string = field.value ?? '';

  // On the label row, beside the field's name — where the design puts it
  // ("Story · 218/500"). Under the box it was the last thing on a tall
  // textarea, so the limit was only discoverable after writing past it.
  //
  // The separator belongs to that pairing, so a field with no visible label
  // keeps the counter at the end of the row without one: prefixed, it read as
  // a stray "· 0/500" hanging above an unlabelled box.
  const showsCounter = showCounter && props.maxLength != null;
  const counter = showsCounter ? (
    <span className="text-[11.5px] font-normal text-muted-foreground">
      {label ? `· ${value.length}/${props.maxLength}` : `${value.length}/${props.maxLength}`}
    </span>
  ) : undefined;

  return (
    <FormField
      label={label}
      htmlFor={inputId}
      required={required}
      error={error}
      help={help}
      labelRight={counter}
      labelRightInline={Boolean(label)}
      className={fieldClassName}
    >
      <Textarea id={inputId} required={required} {...props} {...field} value={value} error={!!error} />
    </FormField>
  );
};
